import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Inbox, HandMetal, ArrowUpCircle, X } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { useToast } from '@/providers/UiProviders'
import { fetchMyQueue, claimNextReport, releaseReport, type MyReport } from './myQueueApi'
import { escalateReport, ESCALATION_LEVELS, type EscalationLevel } from './escalationApi'

/**
 * A6.13 — Mening navbatim. Pull-based ish taqsimoti: moderator "Keyingisini ol"
 * bosadi → server eng-xavfli bo'sh shikoyatni ATOMIK (SKIP LOCKED) biriktiradi,
 * shuning uchun ikki moderator bir shikoyatni ishlamaydi. O'z navbatini ko'radi,
 * ochib qaror qiladi (A6.19), yoki bo'shatadi.
 */

const S = {
  title: 'Mening navbatim',
  desc: "Sizga biriktirilgan, hali yopilmagan shikoyatlar",
  claim: 'Keyingisini ol',
  empty: "Navbatingiz bo'sh. \"Keyingisini ol\" bosing.",
  poolEmpty: "Bo'sh shikoyat qolmadi",
  claimed: 'Yangi shikoyat biriktirildi',
  released: "Shikoyat bo'shatildi",
  failed: 'Amal bajarilmadi',
  id: 'ID',
  category: 'Toifa',
  target: 'Obyekt',
  reason: 'Sabab',
  claimedAt: 'Olingan',
  open: 'Ochish',
  release: "Bo'shatish",
  escalate: 'Eskalatsiya',
  escalatePrompt: `Daraja (${ESCALATION_LEVELS.join(' | ')}):`,
  escalateReasonPrompt: 'Eskalatsiya sababi:',
  escalated: 'Yuqori tierга eskalatsiya qilindi',
  badLevel: "Daraja noto'g'ri",
}

// Toifa ranglari (yuqori-xavf qizil).
const CAT_TONE: Record<string, string> = {
  csam: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  violence: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  fraud: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  doxxing: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
}
function catTone(c: string): string {
  return CAT_TONE[c] ?? 'bg-surface-2 text-secondary border-border-default'
}

function fmtTime(ts?: number): string {
  if (!ts || !Number.isFinite(ts)) return '—'
  try {
    return new Date(ts).toLocaleString()
  } catch {
    return '—'
  }
}

export function MyQueue() {
  const { toast } = useToast()
  const navigate = useNavigate()
  const [rows, setRows] = useState<MyReport[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [claiming, setClaiming] = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    fetchMyQueue(100)
      .then(setRows)
      .catch(() => setRows([]))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const claim = useCallback(async () => {
    setClaiming(true)
    try {
      const rep = await claimNextReport()
      if (!rep) {
        toast(S.poolEmpty, { tone: 'info' })
        return
      }
      toast(S.claimed, { tone: 'success' })
      // Yangi olinganni ro'yxat boshiga qo'shamiz (qayta yuklamasdan).
      setRows((prev) => [rep, ...(prev ?? []).filter((r) => r.id !== rep.id)])
    } catch (e) {
      toast(S.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
    } finally {
      setClaiming(false)
    }
  }, [toast])

  const release = useCallback(
    async (id: number) => {
      try {
        await releaseReport(id)
        toast(S.released, { tone: 'success' })
        setRows((prev) => (prev ? prev.filter((r) => r.id !== id) : prev))
      } catch (e) {
        toast(S.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
      }
    },
    [toast],
  )

  const escalate = useCallback(
    async (id: number) => {
      const level = (window.prompt(S.escalatePrompt, 'senior') ?? '').trim() as EscalationLevel
      if (!level) return
      if (!ESCALATION_LEVELS.includes(level)) {
        toast(S.badLevel, { tone: 'warning' })
        return
      }
      const reason = (window.prompt(S.escalateReasonPrompt) ?? '').trim()
      try {
        await escalateReport(id, level, reason)
        toast(S.escalated, { tone: 'success' })
        // Eskalatsiya qilingan shikoyat mening navbatimдан chiqadi (assigned tozalanadi).
        setRows((prev) => (prev ? prev.filter((r) => r.id !== id) : prev))
      } catch (e) {
        toast(S.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
      }
    },
    [toast],
  )

  return (
    <>
      <PageHeader
        title={S.title}
        screenId="A6.13"
        description={S.desc}
        actions={
          <button
            onClick={() => void claim()}
            disabled={claiming}
            className="inline-flex items-center gap-1.5 rounded-md bg-accent px-4 py-1.5 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50"
          >
            <HandMetal size={14} />
            {S.claim}
          </button>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {rows === null || loading ? (
          <div className="p-6 text-center text-sm text-tertiary">…</div>
        ) : rows.length === 0 ? (
          <div className="px-3 py-10 text-center text-sm text-tertiary">
            <Inbox size={24} className="mx-auto mb-2 opacity-60" />
            {S.empty}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-sm">
              <thead>
                <tr className="border-b border-border-default text-left text-xs text-tertiary">
                  <th className="px-2 py-1.5 font-medium">{S.id}</th>
                  <th className="px-2 py-1.5 font-medium">{S.category}</th>
                  <th className="px-2 py-1.5 font-medium">{S.target}</th>
                  <th className="px-2 py-1.5 font-medium">{S.reason}</th>
                  <th className="px-2 py-1.5 font-medium">{S.claimedAt}</th>
                  <th className="px-2 py-1.5 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    className="cursor-pointer border-b border-border-subtle align-top hover:bg-surface-2"
                    onClick={() => navigate(`/moderation/${r.id}`)}
                  >
                    <td className="px-2 py-1.5 font-mono text-tertiary">#{r.id}</td>
                    <td className="px-2 py-1.5">
                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${catTone(r.category)}`}>
                        {r.category}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 font-mono text-secondary">{r.target_type}#{r.target_id}</td>
                    <td className="max-w-xs truncate px-2 py-1.5 text-secondary" title={r.reason ?? ''}>{r.reason ?? '—'}</td>
                    <td className="whitespace-nowrap px-2 py-1.5 text-tertiary">{fmtTime(r.assigned_at)}</td>
                    <td className="whitespace-nowrap px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1.5">
                        <Link
                          to={`/moderation/${r.id}`}
                          className="inline-flex items-center gap-1 rounded-md bg-accent px-2.5 py-1 text-xs font-medium text-white hover:bg-accent-hover"
                        >
                          {S.open}
                        </Link>
                        <button
                          onClick={() => void escalate(r.id)}
                          className="inline-flex items-center gap-1 rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 text-xs font-medium text-amber-300 hover:bg-amber-500/20"
                        >
                          <ArrowUpCircle size={12} />
                          {S.escalate}
                        </button>
                        <button
                          onClick={() => void release(r.id)}
                          className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-xs font-medium text-secondary hover:text-primary"
                        >
                          <X size={12} />
                          {S.release}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}
